import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { useFormatValue } from "../../../hooks/useFormatValue";
import { translate } from "../../../hooks/translate";
import { useModalConfirm as confirm } from "../../../hooks/useModalConfirm";
import { useAuth } from "../../../services/useAuth";
import { withAuth } from "../../../components/HOC/withAuth";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";

import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import Button from "../../../components/BaseUI/Button";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import { Toast, ToastContainer } from "../../../components/CustomUI/Toast";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
import Input from "../../../components/BaseUI/Input";
import Select from "../../../components/BaseUI/Select";
import { useTitle } from "../../../hooks/useTitle";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiUser,
  FiMail,
  FiLock,
  FiSave,
  FiColumns,
} from "react-icons/fi";
import Lozenge from "../../../components/BaseUI/Lozenge";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
} from "../../../components/BaseUI/Dropdown";
import Checkbox from "../../../components/BaseUI/Checkbox";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

/** GET /users populates profileId as { _id, name }; normalize for compares and PUT/POST */
function profileIdToString(profileId) {
  if (profileId == null || profileId === "") return "";
  if (
    typeof profileId === "object" &&
    profileId !== null &&
    profileId._id != null
  ) {
    return String(profileId._id);
  }
  return String(profileId);
}

function profileIdsEqual(a, b) {
  const sa = profileIdToString(a);
  const sb = profileIdToString(b);
  if (!sa || !sb) return sa === sb;
  return sa === sb;
}

function resolveCompanyId(user, companyList = []) {
  if (!user) return "";
  let id = "";
  if (user.companyId) id = String(user.companyId);
  else if (user.company?.id) id = String(user.company.id);
  else if (user.assignedType === "company" && user.assignedId) {
    id = String(user.assignedId);
  } else if (user.role === "company" && user.assignedId) {
    id = String(user.assignedId);
  }
  if (!id) return "";

  const isObjectId = /^[a-fA-F0-9]{24}$/.test(id);
  if (!isObjectId && companyList.length > 0) {
    const match = companyList.find(
      (c) => String(c._id) === id || c.code === id,
    );
    if (match?._id) return String(match._id);
  }
  return id;
}

const Users = () => {
  useTitle("Rovion MES - Kullanıcılar");
  const navigate = useNavigate();
  const { getToken, getUser, getUserFromToken } = useAuth();
  const format = useFormatValue();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [userData, setUserData] = useState({
    name: "",
    email: "",
    status: "active",
    password: "",
    confirmPassword: "",
    role: "company",
    assignmentType: "company",
    companyId: "",
    modules: [],
    pages: [],
    profileId: "",
    cardNo: "",
  });
  const [formLoading, setFormLoading] = useState(false);
  const [editSidebarOpen, setEditSidebarOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const { user } = useAuth();
  const breadcrumbs = [
    { label: "Rovion", url: "" },
    { label: translate("UI.USERS") },
  ];

  const headers = [
    {
      key: "name",
      name: translate("UI.NAME"),
      dataType: "text",
      id: 0,
      customSorter: (a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), undefined, {
          numeric: true,
          sensitivity: "base",
        }),
    },
    {
      key: "email",
      name: translate("UI.EMAIL"),
      dataType: "text",
      id: 1,
      customSorter: (a, b) =>
        String(a.email || "").localeCompare(String(b.email || ""), undefined, {
          numeric: true,
          sensitivity: "base",
        }),
    },
    {
      key: "profile",
      name: translate("UI.USER_PROFILE"),
      dataType: "text",
      id: 2,
      customSorter: (a, b) => {
        const na =
          (typeof a.profileId === "object" && a.profileId?.name) ||
          profiles.find((p) => profileIdsEqual(p._id, a.profileId))?.name ||
          "";
        const nb =
          (typeof b.profileId === "object" && b.profileId?.name) ||
          profiles.find((p) => profileIdsEqual(p._id, b.profileId))?.name ||
          "";
        return na.localeCompare(nb, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      },
    },
    {
      key: "status",
      name: translate("UI.STATUS"),
      dataType: "text",
      id: 5,
      customSorter: (a, b) =>
        String(a.status || "").localeCompare(String(b.status || "")),
    },
    {
      key: "createdAt",
      name: translate("UI.CREATED"),
      dataType: "date",
      id: 6,
      customSorter: (a, b) => {
        const ta = new Date(a?.createdAt).getTime();
        const tb = new Date(b?.createdAt).getTime();
        return (Number.isFinite(ta) ? ta : 0) - (Number.isFinite(tb) ? tb : 0);
      },
    },
    {
      key: "lastLogin",
      name: translate("UI.LAST_LOGIN"),
      dataType: "date",
      id: 7,
      customSorter: (a, b) => {
        const ta = new Date(a?.lastLogin).getTime();
        const tb = new Date(b?.lastLogin).getTime();
        const fa = Number.isFinite(ta) ? ta : -1;
        const fb = Number.isFinite(tb) ? tb : -1;
        return fa - fb;
      },
    },
    {
      key: "actions",
      name: translate("UI.ACTIONS"),
      dataType: "actions",
      id: 8,
      sortable: false,
    },
  ];

  // Use column visibility hook
  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("users-list", headers);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get user data first to determine filtering
      const userData = await getUserFromToken(true);

      // Construct query parameters based on user role
      const params = {};
      if (userData.role === "company" || resolveCompanyId(userData)) {
        params.companyId = resolveCompanyId(userData);
      }

      const response = await axios.get(`${API_URL}/api/users`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        params,
      });

      // Backend now handles all filtering automatically
      if (response.data?.users && Array.isArray(response.data.users)) {
        setUsers(response.data.users);
      } else {
        console.error("Invalid data structure:", response.data);
        throw new Error("Invalid data structure received from API");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setError(error.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [getToken, getUserFromToken]);

  const fetchProfiles = useCallback(
    async (tenantId = null) => {
      try {
        const uData = await getUserFromToken(true);
        const effectiveTenantId = tenantId || resolveCompanyId(uData);

        if (!effectiveTenantId && uData.role === "admin") {
          setProfiles([]);
          return;
        }

        const params = {};
        if (effectiveTenantId && uData.role === "admin") {
          params.tenantId = effectiveTenantId;
        }

        const res = await axios.get(`${API_URL}/api/user-profiles`, {
          headers: { Authorization: `Bearer ${getToken()}` },
          params,
        });
        setProfiles(res.data?.profiles || []);
      } catch (err) {
        console.error("Error fetching profiles:", err);
        setProfiles([]);
      }
    },
    [getToken, getUserFromToken],
  );

  const fetchCompanies = async () => {
    try {
      const currentUser = await getUserFromToken(true);

      const companiesRes = await axios.get(`${API_URL}/api/system-companies`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      const allCompanies = companiesRes.data || [];
      setCompanies(allCompanies);

      if (currentUser?.role === "admin") {
        setAvailableCompanies(allCompanies);
      } else if (currentUser?.role === "company") {
        const userCompanyId = resolveCompanyId(currentUser);
        const companyOptions = allCompanies.filter((company) => {
          return (
            company._id === userCompanyId || company.code === userCompanyId
          );
        });
        setAvailableCompanies(companyOptions);
      } else {
        setAvailableCompanies([]);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  const addToast = (toast) => {
    const newToast = {
      ...toast,
      id: Date.now(),
    };
    setToasts((currentToasts) => [...currentToasts, newToast]);

    // Remove toast after 8 seconds
    setTimeout(() => {
      removeToast(newToast.id);
    }, 8000);
  };

  const removeToast = (toastId) => {
    setToasts((currentToasts) => currentToasts.filter((t) => t.id !== toastId));
  };

  const handleDelete = async (userId) => {
    confirm({
      title: `${translate("UI.CONFIRM_DELETE")}?`,
      message: translate("UI.CONFIRM_DELETE_USER_MESSAGE"),
      appearance: "danger",
      confirmButtonText: translate("UI.DELETE"),
      showDeactivateOption: true,
      onConfirm: async (deactivateOnly) => {
        try {
          setLoading(true);

          const response = await axios.delete(
            `${API_URL}/api/users/${userId}?deactivateOnly=${deactivateOnly}`,
            {
              headers: {
                Authorization: `Bearer ${getToken()}`,
              },
            },
          );

          if (response.data.success) {
            addToast({
              description: deactivateOnly
                ? translate("UI.USER_DEACTIVATED_SUCCESSFULLY")
                : translate("UI.USER_DELETED_SUCCESSFULLY"),
              title: "Başarılı!",
              appearance: "success",
            });

            await fetchUsers();
          }
        } catch (error) {
          console.error("Status update error:", error);
          addToast({
            description:
              error.response?.data?.message ||
              translate("UI.ERROR_UPDATING_USER"),
            title: "Hata!",
            appearance: "danger",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const getStatusAppearance = (status) => {
    switch (status) {
      case "active":
        return "success-subtle";
      case "inactive":
        return "danger-subtle";
      case "maintenance":
        return "warning-subtle";
      default:
        return "neutral-subtle";
    }
  };
  const getRoleAppearance = (status) => {
    switch (status) {
      case "user":
        return "info-subtle";

      case "company":
        return "success-subtle";
      case "admin":
        return "danger-subtle";
      case "employee":
        return "success-subtle";
      default:
        return "neutral-subtle";
    }
  };
  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (userData.password !== userData.confirmPassword) {
      addToast({
        description: translate("UI.PASSWORDS_DO_NOT_MATCH"),
        title: "Hata!",
        appearance: "danger",
      });
      return;
    }

    try {
      setFormLoading(true);

      const currentUser = (await getUserFromToken(true)) || getUser();
      const isCompanyAccount =
        currentUser?.role === "company" ||
        !!resolveCompanyId(currentUser, companies);
      const resolvedCompanyId =
        resolveCompanyId(currentUser, companies) || userData.companyId || "";

      // Create payload with base data
      const payload = {
        name: userData.name,
        email: userData.email,
        password: userData.password,
        role: userData.assignmentType === "admin" ? "admin" : "company",
        modules: userData.modules || [],
        pages: userData.pages || [],
        profileId: profileIdToString(userData.profileId) || null,
        cardNo: userData.cardNo || "",
      };

      if (isCompanyAccount && resolvedCompanyId) {
        payload.companyId = resolvedCompanyId;
        payload.role = "company";
      } else if (userData.assignmentType === "company") {
        if (userData.companyId) {
          payload.companyId = userData.companyId;
        } else if (userData.company?.id) {
          const company = companies.find(
            (company) => company.code === userData.company?.id,
          );
          payload.companyId = company?._id;
        }
      }

      const response = await axios.post(`${API_URL}/api/users`, payload, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.data.success) {
        fetchUsers();
        setSidebarOpened(false);
        resetForm();
        addToast({
          description: translate("UI.USER_CREATED_SUCCESSFULLY"),
          title: "Başarılı!",
          appearance: "success",
        });
      }
    } catch (error) {
      addToast({
        description:
          error.response?.data?.message || translate("UI.ERROR_CREATING_USER"),
        title: "Hata!",
        appearance: "danger",
      });
    } finally {
      setFormLoading(false);
    }
  };

  const resetForm = () => {
    const currentUser = getUser();
    const companyId = resolveCompanyId(currentUser, companies);

    setUserData({
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      role: "company",
      isAdmin: false,
      assignmentType: "company",
      companyId: companyId || "",
      modules: [],
      pages: [],
      profileId: "",
      cardNo: "",
    });
  };

  const handleEdit = (user) => {
    // Determine assignment type based on user data from User model
    let assignmentType = "system";

    if (
      user.companyId ||
      user.assignedType === "company" ||
      user.role === "company" ||
      user.role === "employee" ||
      user.role === "user"
    ) {
      assignmentType = "company";
    } else if (user.role === "admin") {
      assignmentType = "admin";
    }

    const normalizedRole = user.role === "admin" ? "admin" : "company";

    const editUser = {
      id: user._id,
      name: user.name,
      email: user.email,
      role: normalizedRole,
      isAdmin: user.isAdmin || false,
      assignmentType: assignmentType,
      companyId:
        user.companyId ||
        (user.assignedType === "company" ? user.assignedId : "") ||
        "",
      password: "",
      confirmPassword: "",
      modules: user.modules || [],
      pages: user.pages || [],
      profileId: profileIdToString(user.profileId),
      cardNo: user.cardNo || "",
    };

    setEditingUser(editUser);
    setEditSidebarOpen(true);
  };

  // Auto-set assignment when edit sidebar opens
  useEffect(() => {
    if (editSidebarOpen && editingUser) {
      autoSetAssignment(editingUser, setEditingUser);
    }
  }, [editSidebarOpen, availableCompanies]);

  const handleUpdate = async (e) => {
    e.preventDefault();

    // Validate assignment based on type
    if (editingUser.assignmentType === "company" && !editingUser.companyId) {
      addToast({
        description: "Please select a company",
        title: "Hata!",
        appearance: "danger",
      });
      return;
    }

    // Validate passwords if user is changing password
    if (
      editingUser.password &&
      editingUser.password.trim() !== ""
    ) {
      // Check if password is too short
      if (editingUser.password.trim().length < 6) {
        addToast({
          description: translate("UI.PASSWORD_TOO_SHORT"),
          title: "Hata!",
          appearance: "danger",
        });
        return;
      }

      // Check if passwords match
      if (editingUser.password !== editingUser.confirmPassword) {
        addToast({
          description: translate("UI.PASSWORDS_DO_NOT_MATCH"),
          title: "Hata!",
          appearance: "danger",
        });
        return;
      }
    }

    try {
      setLoading(true);

      // Create base payload
      const payload = {
        name: editingUser.name,
        email: editingUser.email,
        role: editingUser.assignmentType === "admin" ? "admin" : "company",
        modules: editingUser.modules || [],
        pages: editingUser.pages || [],
        profileId: profileIdToString(editingUser.profileId) || null,
        cardNo: editingUser.cardNo || "",
      };

      // Add password to payload if provided
      if (
        editingUser.password &&
        editingUser.password.trim() !== ""
      ) {
        payload.password = editingUser.password.trim();
        payload.updatePassword = true;
      }

      if (editingUser.assignmentType === "company") {
        const companyId = editingUser.companyId || resolveCompanyId(getUser());
        if (companyId) payload.companyId = companyId;
      }

      const response = await axios.put(
        `${API_URL}/api/users/${editingUser.id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data.success) {
        let successMessage = translate("UI.USER_UPDATED_SUCCESSFULLY");
        if (payload.updatePassword) {
          successMessage = translate("UI.PASSWORD_CHANGED_SUCCESSFULLY");
        }

        addToast({
          description: successMessage,
          title: "Başarılı!",
          appearance: "success",
        });

        setEditSidebarOpen(false);
        resetForm();
        fetchUsers();
      } else {
        // Handle unsuccessful response
        addToast({
          description:
            response.data.message || translate("UI.ERROR_UPDATING_USER"),
          title: "Hata!",
          appearance: "danger",
        });
      }
    } catch (error) {
      addToast({
        description:
          error.response?.data?.message || translate("UI.ERROR_UPDATING_USER"),
        title: "Hata!",
        appearance: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to get available assignment types based on current user permissions
  const getAvailableAssignmentTypes = () => {
    const currentUser = getUser();
    const types = [];

    if (currentUser?.role === "admin") {
      types.push(
        { key: "admin", label: translate("UI.ADMIN") },
        { key: "company", label: translate("UI.COMPANY") },
      );
    }

    return types;
  };

  // Function to auto-set assignment based on user role
  const autoSetAssignment = (userData, setUserDataFunc) => {
    const currentUser = getUser();
    const companyId = resolveCompanyId(currentUser, companies);

    if (companyId) {
      setUserDataFunc((prev) => ({
        ...prev,
        assignmentType: "company",
        role: "company",
        companyId,
      }));
    }
    // Admin assignment type doesn't need auto-setting - it's system-wide
  };

  // Function to check if company dropdown should be shown
  const shouldShowCompanyDropdown = (assignmentType) => {
    const currentUser = getUser();

    // For company users, only show dropdown for employee assignment
    if (currentUser?.role === "company") {
      return false;
    }

    if (assignmentType === "admin") {
      return false;
    }

    return assignmentType === "company";
  };

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
    fetchProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userData.companyId) {
      fetchProfiles(userData.companyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData.companyId]);

  useEffect(() => {
    if (editingUser?.companyId) {
      fetchProfiles(editingUser.companyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingUser?.companyId]);

  useEffect(() => {}, [sidebarOpened]);

  // Auto-set assignment when sidebar opens or user data changes
  useEffect(() => {
    if (sidebarOpened) {
      autoSetAssignment(userData, setUserData);
    }
  }, [sidebarOpened, availableCompanies]);

  const handleAddUser = () => {
    setSidebarOpened(true);
  };

  return (
    <AppPage
      // title={translate("UI.USERS")}
      breadcrumbs={breadcrumbs}
      loading={loading}
    >
      <Container>
        <ContainerItem>
          <div className="page-header-card p-3 d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-0">{translate("UI.USERS_LIST")}</h2>
            </div>
            <div className="d-flex gap-2">
              <Button
                title={translate("UI.ADD_USER")}
                appearance="primary-animated"
                icon={<FiPlus />}
                onClick={handleAddUser}
              />
            </div>
          </div>

          {error && <div className="alert alert-danger mb-4">{error}</div>}

          {Array.isArray(users) && users.length > 0 ? (
            <Table
              columns={headers}
              visibleColumns={visibleColumns}
              loading={loading}
              emptyMessage={translate("UI.NO_USERS_FOUND")}
              viewOptions={
                <ListViewOptionsPanel
                  columns={headers}
                  visibleColumns={visibleColumns}
                  onToggleColumn={toggleColumnVisibility}
                  onResetColumns={resetToDefault}
                  showApply={false}
                  showReset={false}
                />
              }
            >
              {users.map((user, index) => {
                const userProfile =
                  (typeof user.profileId === "object" && user.profileId?.name
                    ? user.profileId
                    : null) ||
                  profiles.find((p) =>
                    profileIdsEqual(p._id, user.profileId),
                  ) ||
                  null;

                return (
                  <Tr key={index} id={user?._id || index} data={user}>
                    {visibleColumns.includes(0) && <Td>{user.name || "-"}</Td>}
                    {visibleColumns.includes(1) && <Td>{user.email || "-"}</Td>}
                    {visibleColumns.includes(2) && (
                      <Td>
                        <Lozenge
                          appearance={
                            userProfile ? "success-subtle" : "neutral-subtle"
                          }
                        >
                          {userProfile?.name || "-"}
                        </Lozenge>
                      </Td>
                    )}
                    {visibleColumns.includes(5) && (
                      <Td>
                        <Lozenge appearance={getStatusAppearance(user.status)}>
                          {translate(`UI.${user.status?.toUpperCase()}`)}
                        </Lozenge>
                      </Td>
                    )}
                    {visibleColumns.includes(6) && (
                      <Td>
                        {user.createdAt
                          ? new Date(user.createdAt).toLocaleString("tr-TR", {
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })
                          : "-"}
                      </Td>
                    )}
                    {visibleColumns.includes(7) && (
                      <Td>
                        {user.lastLogin
                          ? new Date(user.lastLogin).toLocaleString("tr-TR", {
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })
                          : "-"}
                      </Td>
                    )}
                    {visibleColumns.includes(8) && (
                      <Td>
                        <div className="d-flex align-items-center gap-1">
                          <IconButton
                            icon={<FiEdit2 style={{ fontSize: "18px" }} />}
                            appearance="subtle"
                            shape="circle"
                            onClick={() => handleEdit(user)}
                          />
                          <IconButton
                            icon={<FiTrash2 style={{ fontSize: "18px" }} />}
                            appearance="subtle"
                            shape="circle"
                            onClick={() => handleDelete(user._id)}
                          />
                        </div>
                      </Td>
                    )}
                  </Tr>
                );
              })}
            </Table>
          ) : (
            <div className="alert alert-warning">
              {translate("UI.NO_USERS_FOUND")}
            </div>
          )}
        </ContainerItem>
      </Container>

      <style>
        {`
          .device-header {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 16px;
            margin-bottom: 24px;
            background: var(--m-card-background-color, #f8f9fa);
            border-radius: 8px;
          }

          .form-container {
            padding: 0 16px;
          }

          .form-group {
            margin-bottom: 20px;
          }

          .form-group:last-child {
            margin-bottom: 0;
          }

          .form-actions {
            margin-top: 24px;
            padding: 16px;
            border-top: 1px solid var(--m-border, #e9ecef);
          }

          .form-control-container {
            background: var(--m-card-background-color, #fff);
            border: 1px solid var(--m-border, #e2e5e8);
            border-radius: 6px;
            transition: all 0.2s ease;
          }

        `}
      </style>

      <SidebarContainer
        title={translate("UI.ADD_USER")}
        show={sidebarOpened}
        onClose={() => {
          setSidebarOpened(false);
          resetForm();
        }}
      >
        <div className="form-container">
          <form onSubmit={handleCreateUser}>
            <div className="row g-3">
              <div className="col-12">
                <Input
                  label={translate("UI.NAME")}
                  value={userData.name}
                  onChange={(value) =>
                    setUserData({ ...userData, name: value })
                  }
                  required
                  prefix={<FiUser />}
                />
              </div>
              <div className="col-12">
                <Input
                  type="email"
                  label={translate("UI.EMAIL")}
                  value={userData.email}
                  onChange={(value) =>
                    setUserData({ ...userData, email: value })
                  }
                  required
                  prefix={<FiMail />}
                />
              </div>
              <div className="col-12">
                <Input
                  type="password"
                  label={translate("UI.PASSWORD")}
                  value={userData.password}
                  onChange={(value) =>
                    setUserData({ ...userData, password: value })
                  }
                  required
                />
              </div>
              <div className="col-12">
                <Input
                  type="password"
                  label={translate("UI.CONFIRM_PASSWORD")}
                  value={userData.confirmPassword}
                  onChange={(value) =>
                    setUserData({ ...userData, confirmPassword: value })
                  }
                  required
                />
              </div>
              <div className="col-12">
                <Input
                  type="text"
                  label="Kart Numarası"
                  value={userData.cardNo}
                  onChange={(value) =>
                    setUserData({ ...userData, cardNo: value })
                  }
                  placeholder="Kullanıcı kart numarasını girin (opsiyonel)"
                />
              </div>

              {/* Profile selector moved below company selector */}

              {/* Assignment Type Selection */}
              {getUser()?.role === "admin" &&
                getAvailableAssignmentTypes().length > 0 && (
                  <div className="col-12">
                    <div className="d-flex align-items-center justify-content-between p-2 w-100">
                      <div className="d-flex align-items-center gap-2">
                        <Lozenge appearance="neutral-subtle">
                          {translate("UI.ROLE")}
                        </Lozenge>
                      </div>
                    </div>
                    <div className="form-control-container">
                      <Dropdown
                        placement="bottom-start"
                        trigger={
                          <div className="d-flex align-items-center justify-content-between p-2 w-100">
                            <div className="d-flex align-items-center gap-2">
                              <Lozenge appearance="neutral-subtle">
                                {getAvailableAssignmentTypes().find(
                                  (t) => t.key === userData.assignmentType,
                                )?.label || translate("UI.SELECT_ROLE")}
                              </Lozenge>
                            </div>
                          </div>
                        }
                      >
                        <DropdownContent className="w-100">
                          {getAvailableAssignmentTypes().map((type) => (
                            <DropdownItem
                              key={type.key}
                              onClick={() =>
                                setUserData((prev) => ({
                                  ...prev,
                                  assignmentType: type.key,
                                  role:
                                    type.key === "admin" ? "admin" : "company",
                                  isAdmin: type.key === "admin",
                                  companyId:
                                    type.key === "company"
                                      ? prev.companyId
                                      : "",
                                  profileId:
                                    type.key === "admin" ? "" : prev.profileId,
                                }))
                              }
                              active={userData.assignmentType === type.key}
                            >
                              {type.label}
                            </DropdownItem>
                          ))}
                        </DropdownContent>
                      </Dropdown>
                    </div>
                  </div>
                )}

              {/* Company Selection - only show when needed */}
              {getUser()?.role === "admin" &&
                shouldShowCompanyDropdown(userData.assignmentType) && (
                  <div className="col-12">
                    <div className="d-flex align-items-center justify-content-between p-2 w-100">
                      <div className="d-flex align-items-center gap-2">
                        <Lozenge appearance="neutral-subtle">
                          {translate("UI.SELECT_COMPANY")}
                        </Lozenge>
                      </div>
                    </div>
                    <div className="form-control-container">
                      <Dropdown
                        placement="bottom-start"
                        trigger={
                          <div className="d-flex align-items-center justify-content-between p-2 w-100">
                            <div className="d-flex align-items-center gap-2">
                              <Lozenge appearance="neutral-subtle">
                                {userData.companyId
                                  ? availableCompanies.find(
                                      (c) => c._id === userData.companyId,
                                    )?.name || translate("UI.SELECT_COMPANY")
                                  : translate("UI.SELECT_COMPANY")}
                              </Lozenge>
                            </div>
                          </div>
                        }
                      >
                        <DropdownContent className="w-100">
                          {(() => {
                            return availableCompanies.length > 0 ? (
                              availableCompanies.map((company) => (
                                <DropdownItem
                                  key={company._id}
                                  onClick={() =>
                                    setUserData((prev) => ({
                                      ...prev,
                                      companyId: company._id,
                                      profileId: "", // Reset profile when company changes
                                      pages: [],
                                    }))
                                  }
                                  active={userData.companyId === company._id}
                                >
                                  {company.name}
                                </DropdownItem>
                              ))
                            ) : (
                              <DropdownItem disabled>
                                No companies available
                              </DropdownItem>
                            );
                          })()}
                        </DropdownContent>
                      </Dropdown>
                    </div>
                  </div>
                )}

              {/* Profile selector - only show for employee and user roles */}
              <div className="col-12">
                <div className="d-flex align-items-center justify-content-between p-2 w-100">
                  <div className="d-flex align-items-center gap-2">
                    <Lozenge appearance="neutral-subtle">
                      Kullanıcı Profili
                    </Lozenge>
                  </div>
                </div>
                <div className="form-control-container">
                  <Dropdown
                    placement="bottom-start"
                    trigger={
                      <div className="d-flex align-items-center justify-content-between p-2 w-100">
                        <div className="d-flex align-items-center gap-2">
                          <Lozenge
                            appearance={
                              userData.profileId
                                ? "success-subtle"
                                : "neutral-subtle"
                            }
                          >
                            {userData.profileId
                              ? profiles.find((p) =>
                                  profileIdsEqual(p._id, userData.profileId),
                                )?.name || "Profil Seç"
                              : "Profil Seç"}
                          </Lozenge>
                        </div>
                      </div>
                    }
                  >
                    <DropdownContent className="w-100">
                      <DropdownItem
                        onClick={() =>
                          setUserData((prev) => ({
                            ...prev,
                            profileId: "",
                            pages: [],
                          }))
                        }
                        active={!userData.profileId}
                      >
                        Profil Seçme
                      </DropdownItem>
                      {profiles.length > 0 ? (
                        profiles.map((profile) => (
                          <DropdownItem
                            key={profile._id}
                            onClick={() =>
                              setUserData((prev) => ({
                                ...prev,
                                profileId: String(profile._id),
                                pages: profile.pages || [],
                              }))
                            }
                            active={profileIdsEqual(
                              userData.profileId,
                              profile._id,
                            )}
                          >
                            {profile.name}
                            {profile.description && (
                              <span
                                className="text-muted ms-2"
                                style={{ fontSize: "11px" }}
                              >
                                {profile.description}
                              </span>
                            )}
                          </DropdownItem>
                        ))
                      ) : (
                        <DropdownItem disabled>
                          Önce firma seçiniz veya profil yok
                        </DropdownItem>
                      )}
                    </DropdownContent>
                  </Dropdown>
                </div>
                {userData.profileId && (
                  <div className="mt-2 px-2">
                    <small className="text-muted">
                      Bu profile{" "}
                      {profiles
                        .find((p) => profileIdsEqual(p._id, userData.profileId))
                        ?.pages?.filter((pg) => !pg.endsWith(":write"))
                        .length || 0}{" "}
                      sayfa erişimi tanımlı.
                    </small>
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>

        <div className="form-actions">
          <Button
            type="submit"
            appearance="primary-animated"
            onClick={handleCreateUser}
            loading={formLoading}
            className="d-flex align-items-center gap-2"
          >
            <FiSave size={16} />
            {translate("UI.SAVE")}
          </Button>
        </div>
      </SidebarContainer>

      <SidebarContainer
        title={translate("UI.EDIT_USER")}
        show={editSidebarOpen}
        onClose={() => setEditSidebarOpen(false)}
      >
        <div className="form-container">
          <form onSubmit={handleUpdate}>
            <div className="row g-3">
              <div className="col-12">
                <Input
                  label={translate("UI.NAME")}
                  value={editingUser?.name || ""}
                  onChange={(value) =>
                    setEditingUser((prev) => ({ ...prev, name: value }))
                  }
                  required
                  prefix={<FiUser />}
                />
              </div>
              <div className="col-12">
                <Input
                  type="email"
                  label={translate("UI.EMAIL")}
                  value={editingUser?.email || ""}
                  onChange={(value) =>
                    setEditingUser((prev) => ({ ...prev, email: value }))
                  }
                  required
                  prefix={<FiMail />}
                />
              </div>

              <div className="col-12">
                <Input
                  type="text"
                  label="Kart Numarası"
                  value={editingUser?.cardNo || ""}
                  onChange={(value) =>
                    setEditingUser((prev) => ({ ...prev, cardNo: value }))
                  }
                  placeholder="Kullanıcı kart numarasını girin (opsiyonel)"
                />
              </div>

              {/* Profile selector moved below company selector */}

              {/* Assignment Type Selection */}
              {getUser()?.role === "admin" &&
                getAvailableAssignmentTypes().length > 0 && (
                  <div className="col-12">
                    <div className="d-flex align-items-center justify-content-between p-2 w-100">
                      <div className="d-flex align-items-center gap-2">
                        <Lozenge appearance="neutral-subtle">
                          {translate("UI.ROLE")}
                        </Lozenge>
                      </div>
                    </div>
                    <div className="form-control-container">
                      <Dropdown
                        placement="bottom-start"
                        trigger={
                          <div className="d-flex align-items-center justify-content-between p-2 w-100">
                            <div className="d-flex align-items-center gap-2">
                              <Lozenge appearance="neutral-subtle">
                                {getAvailableAssignmentTypes().find(
                                  (t) => t.key === editingUser?.assignmentType,
                                )?.label || translate("UI.SELECT_ROLE")}
                              </Lozenge>
                            </div>
                          </div>
                        }
                      >
                        <DropdownContent className="w-100">
                          {getAvailableAssignmentTypes().map((type) => (
                            <DropdownItem
                              key={type.key}
                              onClick={() =>
                                setEditingUser((prev) => ({
                                  ...prev,
                                  assignmentType: type.key,
                                  role:
                                    type.key === "admin" ? "admin" : "company",
                                  isAdmin: type.key === "admin",
                                  companyId:
                                    type.key === "company"
                                      ? prev.companyId
                                      : "",
                                  profileId:
                                    type.key === "admin" ? "" : prev.profileId,
                                }))
                              }
                              active={editingUser?.assignmentType === type.key}
                            >
                              {type.label}
                            </DropdownItem>
                          ))}
                        </DropdownContent>
                      </Dropdown>
                    </div>
                  </div>
                )}

              {/* Company Selection - only show when needed */}
              {getUser()?.role === "admin" &&
                shouldShowCompanyDropdown(editingUser?.assignmentType) && (
                  <div className="col-12">
                    <div className="d-flex align-items-center justify-content-between p-2 w-100">
                      <div className="d-flex align-items-center gap-2">
                        <Lozenge appearance="neutral-subtle">
                          {translate("UI.SELECT_COMPANY")}
                        </Lozenge>
                      </div>
                    </div>
                    <div className="form-control-container">
                      <Dropdown
                        placement="bottom-start"
                        trigger={
                          <div className="d-flex align-items-center justify-content-between p-2 w-100">
                            <div className="d-flex align-items-center gap-2">
                              <Lozenge appearance="neutral-subtle">
                                {editingUser?.companyId
                                  ? availableCompanies.find(
                                      (c) => c._id === editingUser.companyId,
                                    )?.name || translate("UI.SELECT_COMPANY")
                                  : translate("UI.SELECT_COMPANY")}
                              </Lozenge>
                            </div>
                          </div>
                        }
                      >
                        <DropdownContent className="w-100">
                          {(() => {
                            return availableCompanies.length > 0 ? (
                              availableCompanies.map((company) => (
                                <DropdownItem
                                  key={company._id}
                                  onClick={() =>
                                    setEditingUser((prev) => ({
                                      ...prev,
                                      companyId: company._id,
                                    }))
                                  }
                                  active={
                                    editingUser?.companyId === company._id
                                  }
                                >
                                  {company.name}
                                </DropdownItem>
                              ))
                            ) : (
                              <DropdownItem disabled>
                                No companies available
                              </DropdownItem>
                            );
                          })()}
                        </DropdownContent>
                      </Dropdown>
                    </div>
                  </div>
                )}

              {/* Profile selector - only show for employee and user roles */}
              <div className="col-12">
                <div className="d-flex align-items-center justify-content-between p-2 w-100">
                  <div className="d-flex align-items-center gap-2">
                    <Lozenge appearance="neutral-subtle">
                      Kullanıcı Profili
                    </Lozenge>
                  </div>
                </div>
                <div className="form-control-container">
                  <Dropdown
                    placement="bottom-start"
                    trigger={
                      <div className="d-flex align-items-center justify-content-between p-2 w-100">
                        <div className="d-flex align-items-center gap-2">
                          <Lozenge
                            appearance={
                              editingUser?.profileId
                                ? "success-subtle"
                                : "neutral-subtle"
                            }
                          >
                            {editingUser?.profileId
                              ? profiles.find((p) =>
                                  profileIdsEqual(p._id, editingUser.profileId),
                                )?.name || "Profil Seç"
                              : "Profil Seç"}
                          </Lozenge>
                        </div>
                      </div>
                    }
                  >
                    <DropdownContent className="w-100">
                      <DropdownItem
                        onClick={() =>
                          setEditingUser((prev) => ({
                            ...prev,
                            profileId: "",
                            pages: [],
                          }))
                        }
                        active={!editingUser?.profileId}
                      >
                        Profil Seçme
                      </DropdownItem>
                      {profiles.length > 0 ? (
                        profiles.map((profile) => (
                          <DropdownItem
                            key={profile._id}
                            onClick={() =>
                              setEditingUser((prev) => ({
                                ...prev,
                                profileId: String(profile._id),
                                pages: profile.pages || [],
                              }))
                            }
                            active={profileIdsEqual(
                              editingUser?.profileId,
                              profile._id,
                            )}
                          >
                            {profile.name}
                            {profile.description && (
                              <span
                                className="text-muted ms-2"
                                style={{ fontSize: "11px" }}
                              >
                                {profile.description}
                              </span>
                            )}
                          </DropdownItem>
                        ))
                      ) : (
                        <DropdownItem disabled>
                          Önce firma seçiniz veya profil yok
                        </DropdownItem>
                      )}
                    </DropdownContent>
                  </Dropdown>
                </div>
                {editingUser?.profileId && (
                  <div className="mt-2 px-2">
                    <small className="text-muted">
                      Bu profile{" "}
                      {profiles
                        .find((p) =>
                          profileIdsEqual(p._id, editingUser.profileId),
                        )
                        ?.pages?.filter((pg) => !pg.endsWith(":write"))
                        .length || 0}{" "}
                      sayfa erişimi tanımlı.
                    </small>
                  </div>
                )}
              </div>

              {/* Password change section */}
              <>
                <div className="col-12 mt-4 d-flex justify-content-between align-items-center gap-3 mb-2">
                  <div>
                    <h5>{translate("UI.CHANGE_PASSWORD")}</h5>
                    <p className="text-muted small">
                      {translate("UI.CHANGE_PASSWORD_DESCRIPTION")}
                    </p>
                  </div>
                </div>
                <div className="col-12">
                  <Input
                    type="password"
                    label={translate("UI.NEW_PASSWORD")}
                    value={editingUser?.password || ""}
                    onChange={(value) => {
                      setEditingUser((prev) => ({
                        ...prev,
                        password: value,
                      }));
                    }}
                    prefix={<FiLock />}
                    placeholder="Şifre (en az 6 karakter)"
                    className={
                      editingUser?.password && editingUser.password.length < 6
                        ? "border-danger"
                        : ""
                    }
                  />
                  {editingUser?.password &&
                    editingUser.password.length > 0 &&
                    editingUser.password.length < 6 && (
                      <small className="text-danger">
                        {translate("UI.PASSWORD_TOO_SHORT")}
                      </small>
                    )}
                </div>
                <div className="col-12">
                  <Input
                    type="password"
                    label={translate("UI.CONFIRM_PASSWORD")}
                    value={editingUser?.confirmPassword || ""}
                    onChange={(value) => {
                      setEditingUser((prev) => ({
                        ...prev,
                        confirmPassword: value,
                      }));
                    }}
                    prefix={<FiLock />}
                    placeholder="Şifreyi tekrar giriniz"
                    className={
                      editingUser?.password &&
                      editingUser?.confirmPassword &&
                      editingUser.password !== editingUser.confirmPassword
                        ? "border-danger"
                        : ""
                    }
                  />
                  {editingUser?.password &&
                    editingUser?.confirmPassword &&
                    editingUser.password !== editingUser.confirmPassword && (
                      <small className="text-danger">
                        {translate("UI.PASSWORDS_DO_NOT_MATCH")}
                      </small>
                    )}
                </div>
              </>
            </div>
          </form>
        </div>

        <div className="form-actions">
          <Button
            type="submit"
            appearance="primary-animated"
            onClick={handleUpdate}
            loading={loading}
            className="d-flex align-items-center gap-2"
          >
            <FiSave size={16} />
            {translate("UI.SAVE")}
          </Button>
        </div>
      </SidebarContainer>

      <ToastContainer>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            id={toast.id}
            title={toast.title}
            description={toast.description}
            appearance={toast.appearance}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default withAuth(Users);
